import React, { useEffect, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import {
  ageLabel, canArrive, canCancel, canConfirm, canNoShow, canRebook, canRegister, canReschedule, dateOnly, formatDateLabel, slotLabel, statusMeta,
} from '../../utils/appointmentLogic';
import { formatMoney, waitingMinutes, whatsAppLink } from '../../utils/appointmentExtras';

export const StatusPill = ({ status }) => {
  const meta = statusMeta(status);
  return <span className={`apt-pill apt-pill-${meta.tone}`}>{meta.label}</span>;
};

const Row = ({ label, children }) => (
  <div className="apt-detail-row">
    <span className="apt-detail-label">{label}</span>
    <span className="apt-detail-value">{children || <em className="apt-muted">-</em>}</span>
  </div>
);

const actionText = {
  CREATED: 'Booked',
  CONFIRMED: 'Confirmed',
  RESCHEDULED: 'Rescheduled',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'Marked no-show',
  REGISTERED: 'Registered',
  ARRIVED: 'Patient arrived',
  REMINDER_SENT: 'Reminder sent',
};

export const AppointmentDetailModal = ({
  appointment, onClose, perms, busy, onConfirm, onReschedule, onCancel, onNoShow, onRegister, onArrive, onPrint, onReminder, onRebook,
}) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const id = appointment?.appointmentId;
  const version = appointment?.version;
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoadingHistory(true);
    appointmentService
      .history(id)
      .then((res) => { if (!cancelled) setHistory(res.success ? res.data || [] : []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [id, version]);

  if (!appointment) return null;
  const a = appointment;

  return (
    <div className="apt-backdrop" onClick={onClose}>
      <div className="apt-modal apt-modal-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Appointment ${a.appointmentNo}`}>
        <div className="apt-modal-head">
          <h2>{a.appointmentNo} <StatusPill status={a.status} /></h2>
          <button type="button" className="apt-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="apt-modal-body apt-detail-cols">
          <div>
            <h3 className="apt-h3">Appointment</h3>
            <Row label="Date">{formatDateLabel(a.appointmentDate)}</Row>
            <Row label="Slot">{slotLabel(a)}</Row>
            <Row label="Centre">{a.centreName}</Row>
            <Row label="Department">{a.departmentName}</Row>
            <Row label="Visit type">{a.visitType}</Row>
            {a.scheduledDoctorName && <Row label="Consulting doctor">{a.scheduledDoctorName}</Row>}
            <Row label="Referring doctor">{a.doctorName}</Row>
            <Row label="Purpose">{a.purpose}</Row>
            <Row label="Remarks">{a.remarks}</Row>
            {a.visitType && /home/i.test(a.visitType) && (
              <Row label="Collection">{[a.collectionAddress, a.collectionPincode, a.phlebotomistName && `Phlebotomist: ${a.phlebotomistName}`].filter(Boolean).join(' · ')}</Row>
            )}
            {a.tests?.length > 0 && <Row label="Tests">{a.tests.map((t) => t.name).join(', ')}</Row>}
            {a.estimatedAmount !== null && a.estimatedAmount !== undefined && <Row label="Estimate">{formatMoney(a.estimatedAmount)} <span className="apt-muted">(final bill at registration)</span></Row>}
            {a.tokenNo && <Row label="Token"><span className="apt-token">#{a.tokenNo}</span> {a.isWaiting && <span className="apt-waiting">Waiting {waitingMinutes(a)} min</span>}</Row>}
            {a.reminderCount > 0 && <Row label="Reminders">{a.reminderCount} sent{a.reminderSentAt ? `, last ${String(a.reminderSentAt).replace('T', ' ').slice(0, 16)}` : ''}</Row>}
            {a.rescheduleCount > 0 && <Row label="Rescheduled">{a.rescheduleCount} time(s)</Row>}
            {a.status === 'CANCELLED' && <Row label="Cancel reason">{a.cancelReason}</Row>}
            {a.status === 'REGISTERED' && <Row label="Registration">LabNo {a.registeredLabNo} · UHID {a.registeredPatientId}</Row>}
          </div>

          <div>
            <h3 className="apt-h3">Patient</h3>
            <Row label="Name">{`${a.title || ''} ${a.pName}`.trim()}</Row>
            <Row label="UHID">{a.patientId || 'New patient (not yet registered)'}</Row>
            <Row label="Gender / Age">{[a.gender, ageLabel(a)].filter(Boolean).join(' · ')}</Row>
            <Row label="Mobile">{a.mobile}</Row>
            <Row label="Email">{a.email}</Row>

            <h3 className="apt-h3" style={{ marginTop: 16 }}>History</h3>
            {loadingHistory ? <p className="apt-hint">Loading…</p> : (
              <ol className="apt-timeline">
                {history.map((h) => (
                  <li key={h.historyId}>
                    <strong>{actionText[h.action] || h.action}</strong>
                    {h.action === 'RESCHEDULED' && h.oldDate && (
                      <span> from {dateOnly(h.oldDate)} {h.oldSlotStart} to {dateOnly(h.newDate)} {h.newSlotStart}</span>
                    )}
                    {h.reason && <span className="apt-timeline-reason"> · {h.reason}</span>}
                    <div className="apt-muted">{h.actorName || 'system'} · {String(h.actionDate).replace('T', ' ').slice(0, 16)}</div>
                  </li>
                ))}
                {history.length === 0 && <li className="apt-muted">No history yet.</li>}
              </ol>
            )}
          </div>
        </div>

        <div className="apt-modal-foot">
          {perms.print && onPrint && <button type="button" className="apt-btn" onClick={() => onPrint(a)}>Print slip</button>}
          {canReschedule(a) && perms.update && onReminder && whatsAppLink(a) && <button type="button" className="apt-btn" disabled={busy} onClick={() => onReminder(a)}>WhatsApp reminder</button>}
          {canRebook(a) && perms.create && onRebook && <button type="button" className="apt-btn" onClick={() => onRebook(a)}>Rebook</button>}
          {canNoShow(a) && perms.update && <button type="button" className="apt-btn" disabled={busy} onClick={() => onNoShow(a)}>Mark no-show</button>}
          {canCancel(a) && perms.cancel && <button type="button" className="apt-btn apt-btn-danger" disabled={busy} onClick={() => onCancel(a)}>Cancel appointment</button>}
          {canReschedule(a) && perms.update && <button type="button" className="apt-btn" disabled={busy} onClick={() => onReschedule(a)}>Reschedule</button>}
          {canConfirm(a) && perms.update && <button type="button" className="apt-btn" disabled={busy} onClick={() => onConfirm(a)}>Confirm</button>}
          {canArrive(a) && perms.update && perms.register && onArrive && (
            <button type="button" className="apt-btn apt-btn-primary" disabled={busy} onClick={() => onArrive(a)}>Arrived · Register</button>
          )}
          {canRegister(a) && !canArrive(a) && perms.register && <button type="button" className="apt-btn apt-btn-primary" disabled={busy} onClick={() => onRegister(a)}>Register patient</button>}
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailModal;
